import React from 'react';
import { Tooltip, Text, Group, Box } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

interface DisplayNameCellProps {
    displayName: string | undefined | null;
    id: string | undefined | null;
    index: number;
    /** Starting index for display (e.g., (currentPage - 1) * pageSize) */
    startIndex?: number;
}

const getWhitespaceIssues = (value: string | undefined | null): string[] => {
    const issues: string[] = [];
    if (!value || typeof value !== 'string') return issues;

    if (value.includes('\n')) {
        issues.push('contains newline (\\n)');
    }
    if (value.includes('\r')) {
        issues.push('contains carriage return (\\r)');
    }

    if (value.startsWith('\t')) {
        issues.push('starts with tab');
    }
    if (value.endsWith('\t')) {
        issues.push('ends with tab');
    }

    if (value.length > 0) {
        const firstChar = value[0];
        const lastChar = value[value.length - 1];

        const nonStandardWhitespaceRegex = /[\u00A0\u1680\u2000-\u200B\u202F\u205F\u3000\uFEFF]/;
        if (nonStandardWhitespaceRegex.test(firstChar)) {
            const charCode = firstChar.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0');
            issues.push(`starts with non-standard whitespace (U+${charCode})`);
        }
        if (nonStandardWhitespaceRegex.test(lastChar)) {
            const charCode = lastChar.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0');
            issues.push(`ends with non-standard whitespace (U+${charCode})`);
        }
    }

    return issues;
};

/**
 * A reusable cell component for DisplayName columns that shows
 * an approximate row index and entity ID on hover.
 */
export const DisplayNameCell: React.FC<DisplayNameCellProps> = ({
    displayName,
    id,
    index,
    startIndex = 0
}) => {
    const displayIndex = startIndex + index + 1;
    const name = displayName || '—';
    const entityId = id || 'Unknown';
    const issues = getWhitespaceIssues(displayName);
    const hasIssues = issues.length > 0;

    return (
        <Tooltip
            label={
                <Box fz="xs">
                    <div><strong>Row:</strong> ~{displayIndex}</div>
                    <div><strong>ID:</strong> {entityId}</div>
                    {hasIssues && (
                        <Box mt="xs" c="orange.6">
                            <strong>⚠ Warning:</strong> {issues.join(', ')}
                        </Box>
                    )}
                </Box>
            }
            position="top-start"
        >
            <Group gap={4} align="center" wrap="nowrap">
                {hasIssues && (
                    <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
                )}
                <Text
                    size="sm"
                    style={{ cursor: 'default' }}
                >
                    {name}
                </Text>
            </Group>
        </Tooltip>
    );
};
